/**
 * « Préparer mon live » (`tutor/prepare-live`) : ce que `<TutorPrepareLivePage>` montre.
 *
 * Patron B : les lives à préparer (`getTodayTutorLiveOccurrences`) n'ont pas de réducteur ; leur
 * réponse arrive de `useTutorPrepareLiveData` (`raw`), et la mise à jour en vol de l'écran
 * (`updatingKey`). Les matières viennent de `static-data`.
 *
 * `tutorPrepareStageViewModel` dit si le masteur a un stage à préparer (décision du 02/11 : sans stage, l'écran
 * renvoie à « Ma journée ») : la règle de la tâche « Prépare ton live » (`getPrepareLiveTask` — un
 * stage `checkpoint-sb` aujourd'hui ou demain), relue sur les séances du compte comme le fait
 * l'accueil (`preparable`).
 */
import moment from "moment"
import { createSelector } from "reselect"

import { toTutorWeekSessions, type ApiTutorLiveSession } from "../../../adapters/tutor/Home/tutorSessionAdapter"
import {
    toPrepareSubjects,
    toTutorPrepareOccurrences,
    type ApiPrepareOccurrence,
    type TutorPrepareOccurrenceRow,
} from "../../../adapters/tutor/Lives/tutorPrepareLiveAdapter"
import { type ApiSchoolSubject } from "../../../adapters/parent/shared/schoolSubjectAdapter"
import { type UISubject } from "../../../ui/types"
import { selectAccount } from "../../shared/displayedStudent"

export type TutorPrepareLiveArgs = {
    raw: ApiPrepareOccurrence[] | null
    updatingKey: string | null
}

export type TutorPrepareLiveViewModel = {
    occurrences: TutorPrepareOccurrenceRow[]
    subjects: UISubject[]
}

export type TutorPrepareStage = {
    /** Le compte est lu : la règle du stage peut trancher. */
    accountLoaded: boolean
    /** Un stage à préparer aujourd'hui ou demain. */
    hasStage: boolean
}

type Account = { live_sessions?: ApiTutorLiveSession[] | null } | null
type PrepareState = { data?: { staticData?: { school_subjects?: ApiSchoolSubject[] | null } | null } }

const schoolSubjectsOf = (state: unknown) => (state as PrepareState)?.data?.staticData?.school_subjects ?? null

const selectSubjects = createSelector([schoolSubjectsOf], (subjects) => toPrepareSubjects(subjects))

const selectStage = createSelector(
    [
        // Les séances et la présence du compte, pas le compte : il change d'identité à chaque relecture.
        (state: unknown) => (selectAccount(state as never) as Account)?.live_sessions,
        (state: unknown) => selectAccount(state as never) != null,
        schoolSubjectsOf,
        () => new Date().toDateString(),
    ],
    (liveSessions, accountLoaded, subjects): TutorPrepareStage => ({
        accountLoaded,
        hasStage: toTutorWeekSessions(liveSessions, subjects, moment()).some((session) => session.preparable),
    })
)

const selectPrepare = createSelector(
    [
        (_state: unknown, args: TutorPrepareLiveArgs) => args.raw,
        (_state: unknown, args: TutorPrepareLiveArgs) => args.updatingKey,
        schoolSubjectsOf,
        selectSubjects,
    ],
    (raw, updatingKey, schoolSubjects, subjects): TutorPrepareLiveViewModel => ({
        occurrences: toTutorPrepareOccurrences(raw, schoolSubjects, updatingKey),
        subjects,
    })
)

export function tutorPrepareLiveViewModel(state: unknown, args: TutorPrepareLiveArgs): TutorPrepareLiveViewModel {
    return selectPrepare(state, args)
}

/** Le masteur a-t-il un stage à préparer — lu à part : l'écran en décide avant toute lecture. */
export function tutorPrepareStageViewModel(state: unknown): TutorPrepareStage {
    return selectStage(state)
}
